import { Anchor, Button, Card, Group, Modal, Stack, Text } from '@mantine/core';
import { IconArchive } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { showSuccess } from 'src/toasts/toasts';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { formatRelativeDate } from 'src/utils/dateFormatting';
import { ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';
import { ModelGroup, peopleIn } from './groups';
import classes from './ModelList.module.css';
import { legacyFigure } from './modelOutcome';
import { GroupHeading, modelTarget } from './modelParts';
import { useModelActions } from './useModelActions';

type Props = {
  groups: ModelGroup[];
  names: Record<string, string>;
  scope: ScenarioScope;
};

/** Archived models, grouped like the rest, with the two things left to do with each. */
export function ArchivedModels({ groups, names, scope }: Props) {
  const { updateModel, deleteModel } = useModelActions();
  // The model stays named while its dialog closes, so only the open state is cleared.
  const [deleting, setDeleting] = useState<ScenarioSummary | null>(null);
  const [confirming, setConfirming] = useState(false);
  const restore = async (scenario: ScenarioSummary) => {
    try {
      await updateModel({ scenario, update: { archived: false } });
      showSuccess(`${scenario.label} restored`);
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be restored.', error);
    }
  };

  const deletePermanently = async (scenario: ScenarioSummary) => {
    setConfirming(false);
    try {
      await deleteModel(scenario);
      showSuccess(`${scenario.label} deleted`);
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be deleted.', error);
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Archived models are kept with their history until you delete them. Restore one to bring it
        back to the list.
      </Text>
      {groups.map(group => (
        <Stack key={group.key} gap="xs" data-testid="archived-model-group">
          <GroupHeading
            people={peopleIn(group, names)}
            description={`${group.joint ? 'Joint' : 'Own'} · ${group.models.length} archived`}
          />
          {group.models.map(scenario => (
            <ArchivedRow
              key={scenario.id}
              scenario={scenario}
              scope={scope}
              onRestore={() => void restore(scenario)}
              onDelete={() => {
                setDeleting(scenario);
                setConfirming(true);
              }}
            />
          ))}
        </Stack>
      ))}
      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title={`Delete "${deleting?.label}" permanently?`}
        centered
      >
        <Text size="sm">
          The model and its history of changes are removed for good. This can&apos;t be undone.
        </Text>
        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" size="xs" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="red"
            onClick={() => deleting && void deletePermanently(deleting)}
          >
            Delete permanently
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}

function ArchivedRow({
  scenario,
  scope,
  onRestore,
  onDelete,
}: {
  scenario: ScenarioSummary;
  scope: ScenarioScope;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const currency = useCurrency();
  const archived = scenario.archivedAt
    ? `Archived ${formatRelativeDate(scenario.archivedAt)}`
    : 'Archived';
  const detail = [archived, scenario.latestCommitIntent].filter(Boolean).join(' · ');

  return (
    <Card withBorder radius="md" p="md" className={classes.row} data-testid="archived-model-row">
      <Group justify="space-between" gap="lg" wrap="nowrap">
        <Stack gap={2} miw={0}>
          <Group gap="xs" wrap="nowrap">
            <IconArchive size={16} stroke={1.75} color="var(--mantine-color-gray-6)" aria-hidden />
            {/* Still opens, so its history can be read before it is restored. */}
            <Anchor
              component={Link}
              to={modelTarget(scenario, scope)}
              size="sm"
              fw={600}
              c="gray.10"
              lineClamp={1}
            >
              {scenario.label}
            </Anchor>
          </Group>
          <Text size="xs" c="dimmed" lineClamp={1} title={detail}>
            {detail}
          </Text>
        </Stack>
        <Group gap="lg" wrap="nowrap">
          <Stack gap={0} align="flex-end">
            <Text size="md" fw={600} c="dimmed">
              {legacyFigure(scenario, currency)}
            </Text>
            <Text size="xs" c="dimmed">
              Legacy when archived
            </Text>
          </Stack>
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={onRestore}
            aria-label={`Restore ${scenario.label}`}
          >
            Restore
          </Button>
          <Button
            variant="subtle"
            color="red"
            size="compact-sm"
            onClick={onDelete}
            aria-label={`Delete ${scenario.label} permanently`}
          >
            Delete permanently
          </Button>
        </Group>
      </Group>
    </Card>
  );
}
